import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HelpTip,
  PageTitle,
} from '@dataroom/shared';
import { useBillingPortal, useTenantPlan } from '@/api/hooks';
import { AddArchivePackModal } from '@/components/AddArchivePackModal';
import { ArchivePackLine } from '@/components/ArchivePackLine';
import { UpgradePlanModal } from '@/components/UpgradePlanModal';
import { askCapLabel, billingErrorMessage, TIER_LABELS } from '@/lib/plan';

/** One usage row: "Active rooms 3 / 10" with a small meter that turns red at the limit. */
function UsageMeter({ label, used, limit }: { label: string; used: number; limit: number }) {
  const atLimit = limit > 0 ? used >= limit : used > 0;
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : used > 0 ? 100 : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span>{label}</span>
        <span className={atLimit ? 'font-medium text-destructive' : 'text-muted-foreground'}>
          {used} / {limit}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={Math.min(used, limit)}
        className="h-2 overflow-hidden rounded-full bg-muted"
      >
        <div
          className={`h-full rounded-full ${atLimit ? 'bg-destructive' : 'bg-primary'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Plan & Usage — the tenant's tier and how much of each metered limit is used,
 * plus the billing actions (Stripe portal, upgrade, archive packs). Billing
 * actions are owner-only server-side and answer 503 while Stripe is dormant; both
 * surface as plain messages here, never a crash.
 */
export function BillingPage() {
  const plan = useTenantPlan();
  const portal = useBillingPortal();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [packOpen, setPackOpen] = useState(false);

  if (plan.error instanceof ApiError && plan.error.status === 401) {
    return <Navigate to="/login" replace />;
  }

  const data = plan.data;
  const portalError = billingErrorMessage(portal.error);

  return (
    <div className="mx-auto w-full max-w-container px-4 py-6 md:px-6 space-y-6">
      <PageTitle>Billing</PageTitle>
      <Card>
        <CardHeader>
          <CardTitle>
            Plan &amp; Usage <HelpTip id="admin.billing.usage" className="ml-1" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {plan.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : plan.error || !data ? (
            <p className="text-sm text-destructive">
              {plan.error instanceof ApiError && plan.error.status === 403
                ? 'Only tenant admins can view the plan.'
                : (plan.error?.message ?? 'Request failed.')}
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground">Current plan:</span>
                <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                  {TIER_LABELS[data.tier]}
                </span>
                {data.billingInterval ? (
                  <span className="text-xs text-muted-foreground">
                    {data.billingInterval === 'year'
                      ? '12-month commitment, billed yearly'
                      : 'Month to month'}
                  </span>
                ) : null}
              </div>
              {data.priceCohort === 'founding' ? (
                <p className="text-sm text-muted-foreground">
                  Founding price — kept while you stay subscribed.
                </p>
              ) : null}

              <div className="space-y-4">
                <UsageMeter
                  label="Active rooms"
                  used={data.usage.activeRooms}
                  limit={data.limits.activeRooms}
                />
                <UsageMeter
                  label="Archived"
                  used={data.usage.archivedRooms}
                  limit={data.limits.archivedRooms}
                />
                <UsageMeter
                  label="Admin seats"
                  used={data.usage.adminSeats}
                  limit={data.limits.adminSeats}
                />
                <div className="flex items-center justify-between text-sm">
                  <span className="inline-flex items-center gap-1">
                    <span>Ask the Data Room</span>
                    <HelpTip id="admin.billing.ask-limit" className="ml-1" />
                  </span>
                  <span className="text-muted-foreground">{askCapLabel(data.limits.askPerDay)}</span>
                </div>
              </div>
              {data.archivePacks ? (
                <p className="text-sm text-muted-foreground">
                  Includes {data.archivePacks} archive {data.archivePacks === 1 ? 'pack' : 'packs'}{' '}
                  (the Archived limit above counts their rooms).
                </p>
              ) : null}
              <ArchivePackLine className="text-sm text-muted-foreground" />

              <div className="flex flex-wrap items-center gap-2 border-t pt-4">
                <Button size="sm" onClick={() => setUpgradeOpen(true)}>
                  Upgrade
                </Button>
                <Button size="sm" variant="outline" onClick={() => setPackOpen(true)}>
                  Add archive pack
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => portal.mutate()}
                  disabled={portal.isPending}
                >
                  {portal.isPending ? 'Opening…' : 'Manage billing'}
                </Button>
              </div>
              {portalError ? <p className="text-sm text-destructive">{portalError}</p> : null}
            </>
          )}
        </CardContent>
      </Card>

      {upgradeOpen ? (
        <UpgradePlanModal currentTier={data?.tier} onClose={() => setUpgradeOpen(false)} />
      ) : null}
      {packOpen ? (
        <AddArchivePackModal
          currentPacks={data?.archivePacks ?? 0}
          onClose={() => setPackOpen(false)}
        />
      ) : null}
    </div>
  );
}
